import React, { useState, useEffect, useRef } from 'react';
import './create_project_dialog.css';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { changeProjectEvent, changeProjectProblem, selectEvent } from '../../../redux/project/projects';
import { createEventAPI } from '../../../api/event';
import { CreateProblem } from '../../../api/problem';
import { SortProblemList } from '../../../functions/problem_sorting';
import { showAlert } from '../../../functions/show_alert';
import { HideProgressAlert, showProgressAlert } from '../../../functions/show_progress_page';
import { hundleRequestFailure } from '../../../functions/hundle_request_failure';
import EventWizard from './project_wizard/event_wizard';
import CreationOfEvent from '../create_event/create_event';
import AppBarWidget from '../../../widgets/app_bar/app_bar';

export default function CreateEventDialog({ projectSelected, onClose, onWizardStateChange }) {
    const [selectedOption, setSelectedOption] = useState('manual');
    const [showWizard, setShowWizard] = useState(false);
    const [showManualForm, setShowManualForm] = useState(false);
    const dispatch = useDispatch();
    const hasNotifiedParent = React.useRef(false);

    // Notify parent when wizard or manual form state changes
    useEffect(() => {
        if (onWizardStateChange && (showWizard || showManualForm)) {
            if (!hasNotifiedParent.current) {
                onWizardStateChange(true);
                hasNotifiedParent.current = true;
            }
        } else if (onWizardStateChange && !showWizard && !showManualForm && hasNotifiedParent.current) {
            onWizardStateChange(false);
            hasNotifiedParent.current = false;
        }
    }, [showWizard, showManualForm, onWizardStateChange]);

    const handleOptionChange = (option) => {
        setSelectedOption(option);
    };

    const handleContinue = async () => {
        if (selectedOption === 'manual') {
            // Option 1: Create event manually - show the manual form
            // Set state first, then notify parent in next tick to ensure state is preserved
            setShowManualForm(true);
            // Use setTimeout to ensure state is set before parent re-renders
            setTimeout(() => {
                if (onWizardStateChange) {
                    onWizardStateChange(true);
                }
            }, 0);
        } else if (selectedOption === 'wizard') {
            // Option 2: Show wizard
            // Set state first, then notify parent in next tick to ensure state is preserved
            setShowWizard(true);
            // Use setTimeout to ensure state is set before parent re-renders
            setTimeout(() => {
                if (onWizardStateChange) {
                    onWizardStateChange(true);
                }
            }, 0);
        }
    };

    const handleManualFormBack = () => {
        setShowManualForm(false);
        onClose();
    };

    const handleWizardClose = () => {
        setShowWizard(false);
        onClose();
    };

    const handleWizardComplete = async (eventData) => {
        // Handle wizard completion - create event and optionally problem, solution, etc.
        showProgressAlert();

        if (!projectSelected || !projectSelected.id) {
            HideProgressAlert();
            showAlert(false, 'Project is required');
            return;
        }

        // Create event if event data exists
        if (eventData.eventData && Object.keys(eventData.eventData).length > 0) {
            const eventFormData = eventData.eventData;
            const eventResponse = await createEventAPI(
                eventFormData.eventName,
                eventFormData.location,
                eventFormData.eventDesc,
                eventFormData.negative,
                eventFormData.problem,
                projectSelected.id,
                eventFormData.file || null,
                eventFormData.eventType,
                eventFormData.eventDate,
                eventFormData.youtubeLink || null
            );

            if (eventResponse.statusCode === 200) {
                const createdEvent = eventResponse.response;
                dispatch(changeProjectEvent({ id: projectSelected.id, event: createdEvent }));
                dispatch(selectEvent({ eventId: createdEvent.id }));

                // Create problem if problem data exists
                if (eventData.problemData &&
                    eventData.problemData.pickedPMT &&
                    eventData.problemData.pickedPMT.length > 0 &&
                    eventData.problemData.pickedPD &&
                    eventData.problemData.pickedPD.length > 0) {

                    const sortedProblemData = SortProblemList(
                        eventData.problemData.pickedPMT,
                        eventFormData.eventType
                    );

                    const problemResponse = await CreateProblem(
                        projectSelected.id,
                        eventData.problemData.pickedPD,
                        sortedProblemData,
                        eventFormData.eventType
                    );

                    HideProgressAlert();

                    if (problemResponse.statusCode < 300) {
                        dispatch(changeProjectProblem({ id: projectSelected.id, problem: problemResponse.response }));

                        // Store the problem ID in localStorage for this event so it can be found later
                        const problemId = problemResponse.response?.id;
                        if (problemId && createdEvent?.id) {
                            const getStorageKey = () => `eventProblems_${projectSelected.id || 'default'}`;
                            try {
                                const stored = localStorage.getItem(getStorageKey());
                                const eventProblemMap = stored ? new Map(JSON.parse(stored)) : new Map();
                                eventProblemMap.set(createdEvent.id, problemId);
                                localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(eventProblemMap.entries())));
                            } catch (error) {
                                console.error('Failed to save event problem map:', error);
                            }
                        }

                        showAlert(true, 'Event and Problem Created Successfully!');
                    } else {
                        hundleRequestFailure(problemResponse);
                        showAlert(true, 'Event Created, but Problem creation failed');
                    }
                } else {
                    HideProgressAlert();
                    showAlert(true, 'Event Created Successfully!');
                }
            } else {
                HideProgressAlert();
                hundleRequestFailure(eventResponse);
            }
        } else {
            HideProgressAlert();
        }

        setShowWizard(false);
        onClose();
    };

    if (showManualForm) {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <AppBarWidget />
                <CreationOfEvent projectSelected={projectSelected} onBack={handleManualFormBack} />
            </div>
        );
    }

    if (showWizard) {
        return <EventWizard projectSelected={projectSelected} onClose={handleWizardClose} onComplete={handleWizardComplete} isManual={selectedOption === 'manual'} />;
    }

    return (
        <div className="create-project-dialog-overlay" onClick={(e) => {
            if (e.target.className === 'create-project-dialog-overlay') {
                onClose();
            }
        }}>
            <div className="create-project-dialog">
                <div className="dialog-header">
                    <h2 className="dialog-title">Create New Event</h2>
                    <CloseIcon className="close-icon" onClick={onClose} />
                </div>
                <div className="dialog-content">
                    <div className="radio-options">
                        <label className={`radio-option ${selectedOption === 'manual' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="eventCreation"
                                value="manual"
                                checked={selectedOption === 'manual'}
                                onChange={() => handleOptionChange('manual')}
                            />
                            <span className="radio-label">Create event manually</span>
                        </label>
                        <label className={`radio-option ${selectedOption === 'wizard' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="eventCreation"
                                value="wizard"
                                checked={selectedOption === 'wizard'}
                                onChange={() => handleOptionChange('wizard')}
                            />
                            <span className="radio-label">Create event using step wizard</span>
                        </label>
                    </div>
                </div>
                <div className="dialog-footer">
                    <button className="footer-button cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="footer-button continue" onClick={handleContinue}>
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}
